import type { OrchestratorAgent, SimpleAgentConfig } from '@demo-portal/shared';
import React, { useEffect, useState } from 'react';

interface AgentDiscoverySequenceProps {
  orchestrator: OrchestratorAgent;
  agents: SimpleAgentConfig;
  discoveryDelay?: number;
  onDiscoveryComplete: (discoveredAgents: string[]) => void;
  onInitializationStep: (step: string) => void;
}

interface AgentDiscoveryState {
  currentStep: number;
  discoveredAgents: string[];
  isComplete: boolean;
  currentAgent: string | null;
}

const AgentDiscoverySequence: React.FC<AgentDiscoverySequenceProps> = ({
  orchestrator,
  agents,
  discoveryDelay = 800,
  onDiscoveryComplete,
  onInitializationStep,
}) => {
  const [discoveryState, setDiscoveryState] = useState<AgentDiscoveryState>({
    currentStep: 0,
    discoveredAgents: [],
    isComplete: false,
    currentAgent: null,
  });

  useEffect(() => {
    const runDiscoverySequence = async () => {
      // Step 1: Run through initialization steps
      for (let i = 0; i < orchestrator.initializationSteps.length; i++) {
        const step = orchestrator.initializationSteps[i];
        onInitializationStep(step);
        setDiscoveryState(prev => ({ ...prev, currentStep: i }));
        
        // Wait for step completion
        await new Promise(resolve => setTimeout(resolve, discoveryDelay));
      }

      // Step 2: Discover agents one by one
      const discoveredAgents: string[] = [];
      
      for (let i = 0; i < agents.length; i++) {
        const agent = agents[i];
        setDiscoveryState(prev => ({ 
          ...prev, 
          currentAgent: agent.id,
          currentStep: orchestrator.initializationSteps.length + i 
        }));
        
        onInitializationStep(`Connecting to ${agent.name}...`);
        
        // Simulate connection delay
        await new Promise(resolve => setTimeout(resolve, discoveryDelay));
        
        discoveredAgents.push(agent.id);
        setDiscoveryState(prev => ({ 
          ...prev, 
          discoveredAgents: [...discoveredAgents] 
        }));
        
        onInitializationStep(`✓ ${agent.name} connected and ready`);
        
        // Brief pause between agents
        await new Promise(resolve => setTimeout(resolve, 400));
      }

      // Step 3: Final system ready
      onInitializationStep("🎯 All agents connected - System ready for orchestrated processing");
      
      setDiscoveryState(prev => ({ 
        ...prev, 
        isComplete: true,
        currentAgent: null 
      }));
      
      // Notify completion
      onDiscoveryComplete(discoveredAgents);
    };

    runDiscoverySequence();
  }, [orchestrator, agents, discoveryDelay, onDiscoveryComplete, onInitializationStep]);

  const getProgressPercentage = () => {
    const totalSteps = orchestrator.initializationSteps.length + agents.length;
    return Math.min(100, (discoveryState.currentStep / totalSteps) * 100);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      borderRadius: '15px',
      padding: '20px',
      marginBottom: '20px',
      color: 'white',
      boxShadow: '0 8px 25px rgba(240, 147, 251, 0.3)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px',
        position: 'relative',
        zIndex: 1,
      }}>
        <span style={{ 
          fontSize: '1.8em', 
          marginRight: '12px',
          animation: discoveryState.isComplete ? 'none' : 'spin 2s linear infinite',
        }}>
          🔄
        </span>
        <div>
          <div style={{
            fontWeight: 'bold',
            fontSize: '1.1em',
            marginBottom: '4px',
          }}>
            Agent Discovery in Progress
          </div>
          <div style={{
            fontSize: '0.9em',
            opacity: 0.9,
          }}>
            Initializing multi-agent orchestration system
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '10px',
        height: '8px',
        marginBottom: '20px',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          background: 'linear-gradient(90deg, #fff, rgba(255,255,255,0.8))',
          height: '100%',
          borderRadius: '10px',
          width: `${getProgressPercentage()}%`,
          transition: 'width 0.5s ease',
          boxShadow: '0 0 10px rgba(255,255,255,0.5)',
        }} />
      </div>

      {/* Discovery status */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        marginBottom: '15px',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          padding: '12px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <div style={{ fontSize: '0.8em', opacity: 0.8, marginBottom: '4px' }}>
            Initialization Steps
          </div>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
            {Math.min(discoveryState.currentStep + 1, orchestrator.initializationSteps.length)} / {orchestrator.initializationSteps.length}
          </div>
        </div>
        
        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          padding: '12px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <div style={{ fontSize: '0.8em', opacity: 0.8, marginBottom: '4px' }}>
            Agents Discovered
          </div>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
            {discoveryState.discoveredAgents.length} / {agents.length}
          </div>
        </div>
      </div>

      {/* Currently discovering agent */}
      {discoveryState.currentAgent && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.2)',
          padding: '12px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.9em',
          }}>
            <span style={{ 
              marginRight: '8px',
              animation: 'pulse 1s infinite',
            }}>
              🔍
            </span>
            <span>
              Connecting to: <strong>
                {agents.find(a => a.id === discoveryState.currentAgent)?.name}
              </strong>
            </span>
          </div>
        </div>
      )}

      {/* Discovered agents list */}
      {discoveryState.discoveredAgents.length > 0 && (
        <div style={{
          marginTop: '15px',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            fontSize: '0.85em',
            fontWeight: 'bold',
            marginBottom: '8px',
            opacity: 0.9,
          }}>
            Connected Agents:
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
          }}>
            {discoveryState.discoveredAgents.map((agentId) => {
              const agent = agents.find(a => a.id === agentId);
              return agent ? (
                <div
                  key={agentId}
                  style={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: '500',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span>{agent.avatar}</span>
                  <span>{agent.name.split(' ')[0]}</span>
                  <span style={{ color: '#4ade80' }}>✓</span>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Completion indicator */}
      {discoveryState.isComplete && (
        <div style={{
          marginTop: '15px',
          padding: '12px',
          background: 'rgba(74, 222, 128, 0.2)',
          border: '1px solid rgba(74, 222, 128, 0.4)',
          borderRadius: '10px',
          textAlign: 'center',
          fontWeight: 'bold',
          position: 'relative',
          zIndex: 1,
        }}>
          🎉 Discovery Complete - System Ready!
        </div>
      )}
    </div>
  );
};

export default AgentDiscoverySequence;